import React from 'react'
import PropTypes from 'prop-types'
import { Select as MuiSelect, FormControl, InputLabel } from '@mui/material'
import { styled } from '@mui/material/styles'

/**
 * Styled MUI Select component with white background
 */
const StyledSelect = styled(MuiSelect)(({ theme }) => ({
  backgroundColor: '#ffffff',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#ccc',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#999',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  '& .MuiSelect-select': {
    backgroundColor: '#ffffff',
  },
}))

/**
 * Styled FormControl with consistent spacing
 */
const StyledFormControl = styled(FormControl, {
  shouldForwardProp: (prop) => prop !== 'noMargin',
})(({ theme, noMargin }) => ({
  marginBottom: noMargin ? 0 : theme.spacing(2), // Add bottom margin for spacing between fields unless noMargin is true
}))

/**
 * Shared MUI Select wrapper component
 * Replaces muicss-r18 Select pattern
 */
export default function MuiSelectWrapper ({
  label,
  name,
  value,
  onChange,
  className,
  disabled = false,
  required = false,
  placeholder,
  noMargin = false,
  children,
  ...otherProps
}) {
  // Handle MUI Select onChange signature properly
  const handleChange = (event) => {
    // Find the selected MenuItem to extract data attributes
    let selectedOption = null
    let dataAttributes = {}

    // Handle both single child and array of children
    const childArray = React.Children.toArray(children)
    selectedOption = childArray.find(
      (child) => child.props && child.props.value === event.target.value,
    )

    if (selectedOption && selectedOption.props) {
      dataAttributes = selectedOption.props
    }

    // Create a synthetic event that matches the expected signature
    const syntheticEvent = {
      target: {
        name,
        value: event.target.value,
        selectedIndex: 0,
        // Add getAttribute method for compatibility with components that use it
        getAttribute: (attr) => {
          if (attr === 'name') return name
          if (attr === 'value') return event.target.value
          // Handle data attributes from MenuItem props
          if (attr.startsWith('data-')) {
            return dataAttributes[attr] || null
          }
          return null
        },
        // Add options array for compatibility with legacy select handlers that use target.options[target.selectedIndex]
        options: [
          {
            getAttribute: (attr) => {
              if (attr.startsWith('data-')) {
                return dataAttributes[attr] || null
              }
              return null
            },
          },
        ],
      },
      preventDefault: () => {}, // Add preventDefault method for compatibility
    }

    onChange(syntheticEvent)
  }

  return (
    <StyledFormControl
      fullWidth
      className={className}
      disabled={disabled}
      required={required}
      noMargin={noMargin}
    >
      <InputLabel id={`${name}-label`}>{label}</InputLabel>
      <StyledSelect
        labelId={`${name}-label`}
        id={name}
        name={name}
        value={value !== undefined && value !== null ? value : ''}
        onChange={handleChange}
        label={label}
        placeholder={placeholder}
        {...otherProps}
      >
        {children}
      </StyledSelect>
    </StyledFormControl>
  )
}

MuiSelectWrapper.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  noMargin: PropTypes.bool,
  children: PropTypes.node,
}

MuiSelectWrapper.defaultProps = {
  value: '',
  disabled: false,
  required: false,
}
